import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useHelp } from '../../context/HelpContext';
import HelpPulseRing from './HelpPulseRing';
import HelpStepCard from './HelpStepCard';
import HelpCompletionCelebration from './HelpCompletionCelebration';

const HelpSpotlight = () => {
    const {
        isTourActive,
        currentStep,
        currentStepIndex,
        totalSteps,
        nextStep,
        prevStep,
        stopTour,
    } = useHelp();

    const location = useLocation();
    const [targetRect, setTargetRect] = useState(null);
    const [showCelebration, setShowCelebration] = useState(false);
    const [routeAtStart, setRouteAtStart] = useState(null);

    // Track route at tour start, stop on route change
    useEffect(() => {
        if (isTourActive && !routeAtStart) {
            setRouteAtStart(location.pathname);
        }
        if (!isTourActive) {
            setRouteAtStart(null);
        }
    }, [isTourActive, location.pathname, routeAtStart]);

    useEffect(() => {
        if (isTourActive && routeAtStart && location.pathname !== routeAtStart) {
            stopTour();
        }
    }, [location.pathname, isTourActive, routeAtStart, stopTour]);

    // Find and track target element
    useEffect(() => {
        if (!isTourActive || !currentStep) {
            setTargetRect(null);
            return;
        }

        const findAndTrack = () => {
            // Find the first VISIBLE matching element (handles responsive hidden elements)
            const candidates = document.querySelectorAll(currentStep.target);
            const el = Array.from(candidates).find(e => {
                const r = e.getBoundingClientRect();
                return r.width > 0 && r.height > 0;
            });
            if (!el) {
                // No visible element found — skip to next step
                if (currentStepIndex < totalSteps - 1) {
                    nextStep();
                } else {
                    stopTour();
                }
                return null;
            }

            // Scroll into view
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

            const updateRect = () => {
                const rect = el.getBoundingClientRect();
                setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
            };

            // Initial position after scroll settles
            setTimeout(updateRect, 350);

            // Recalculate on scroll and resize
            const onScroll = () => requestAnimationFrame(updateRect);
            window.addEventListener('scroll', onScroll, true);
            window.addEventListener('resize', onScroll);

            return () => {
                window.removeEventListener('scroll', onScroll, true);
                window.removeEventListener('resize', onScroll);
            };
        };

        // Small delay for DOM to be ready (tab switches, lazy rendering)
        const timer = setTimeout(() => {
            const cleanup = findAndTrack();
            // Store cleanup for later
            if (cleanup) {
                cleanupRef.current = cleanup;
            }
        }, 100);

        const cleanupRef = { current: null };

        return () => {
            clearTimeout(timer);
            if (cleanupRef.current) cleanupRef.current();
        };
    }, [isTourActive, currentStep, currentStepIndex, totalSteps, nextStep, stopTour]);

    // Handle close with celebration on last step
    const handleClose = useCallback(() => {
        if (currentStepIndex >= totalSteps - 1) {
            setShowCelebration(true);
            stopTour();
        } else {
            stopTour();
        }
    }, [currentStepIndex, totalSteps, stopTour]);

    // Handle next with celebration on finish
    const handleNext = useCallback(() => {
        if (currentStepIndex >= totalSteps - 1) {
            setShowCelebration(true);
            stopTour();
        } else {
            nextStep();
        }
    }, [currentStepIndex, totalSteps, nextStep, stopTour]);

    const handleCelebrationComplete = useCallback(() => {
        setShowCelebration(false);
    }, []);

    // ESC key to close
    useEffect(() => {
        if (!isTourActive) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') stopTour();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isTourActive, stopTour]);

    if (!isTourActive && !showCelebration) return null;

    return createPortal(
        <>
            {isTourActive && (
                <div className="fixed inset-0 z-[300] pointer-events-none">
                    <HelpPulseRing rect={targetRect} />
                    <HelpStepCard
                        step={currentStep}
                        stepIndex={currentStepIndex}
                        totalSteps={totalSteps}
                        targetRect={targetRect}
                        onNext={handleNext}
                        onPrev={prevStep}
                        onClose={handleClose}
                    />
                </div>
            )}
            {showCelebration && (
                <HelpCompletionCelebration onComplete={handleCelebrationComplete} />
            )}
        </>,
        document.body
    );
};

export default HelpSpotlight;
